import { useState, useEffect, useCallback } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Plus, Edit2, Trash2, Save, X,
  FileText, Search, Receipt, CheckCircle,
  AlertTriangle, TrendingUp, DollarSign,
  BookOpen, Activity, Printer, FileDown,
  ArrowLeftRight, Wallet, School, BarChart3
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

// ============================================================================
// TYPES
// ============================================================================

interface BudgetYear {
  id: number;
  name: string;
  description?: string;
  school_id: number;
  academic_year_id: number;
  total_planned: number;
  total_actual: number;
  total_remaining: number;
  status: 'draft' | 'active' | 'closed' | 'archived';
  created_at?: string;
  updated_at?: string;
}

interface BudgetMainLine {
  id: number;
  budget_year_id: number;
  order_index: number;
  code: string;
  name: string;
  description?: string;
  planned_amount: number;
  actual_amount: number;
  remaining_amount: number;
}

interface BudgetExpense {
  id: number;
  budget_year_id: number;
  main_line_id: number;
  sub_line_id?: number;
  expense_number: string;
  name: string;
  description?: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  receipt_number?: string;
  receipt_path?: string;
  status: 'pending' | 'verified' | 'rejected' | 'paid';
  expense_date: string;
  payment_method?: string;
  beneficiary?: string;
  verified_by?: number;
  verified_at?: string;
}

interface BudgetFeeLine {
  id: number;
  budget_year_id: number;
  order_index: number;
  code: string;
  name: string;
  description?: string;
  rate_1st_cycle: number;
  rate_2nd_cycle: number;
  is_mandatory: boolean;
  planned_amount: number;
  actual_amount: number;
  remaining_amount: number;
  students_1st_cycle: number;
  students_2nd_cycle: number;
}

interface BudgetFeeExpense {
  id: number;
  fee_line_id: number;
  budget_year_id: number;
  expense_number: string;
  name: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  receipt_number?: string;
  expense_date: string;
  beneficiary?: string;
  is_verified: boolean;
  verified_by?: number;
  verified_at?: string;
}

interface BudgetStats {
  total_planned: number;
  total_actual: number;
  total_remaining: number;
  execution_rate: number;
  line_count: number;
  expense_count: number;
  pending_expenses: number;
  verified_expenses: number;
}

interface FeeStats {
  rate_1st_cycle_total: number;
  rate_2nd_cycle_total: number;
  total_planned: number;
  total_actual: number;
  total_remaining: number;
  execution_rate: number;
  line_count: number;
  expense_count: number;
}

type TabType = 'overview' | 'apee-lines' | 'apee-expenses' | 'fees' | 'reports';

// ============================================================================
// CONFIG
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const LS_TOKEN_KEYS = ['auth_token', 'token', 'access_token', 'jwt'];

// ============================================================================
// TOKEN UTILITAIRE — en dehors du composant pour éviter les problèmes de scope
// ============================================================================

function getToken(): string {
  // DEBUG: logger toutes les clés
  const allKeys = Object.keys(localStorage);
  console.log('[getToken] localStorage keys:', allKeys);

  // 1. localStorage direct
  for (const key of LS_TOKEN_KEYS) {
    const val = localStorage.getItem(key);
    if (val && val !== 'null' && val !== 'undefined' && val.length > 10) {
      console.log('[getToken] Token trouvé direct sous', key);
      return val;
    }
  }

  // 2. Zustand persist — chercher dans auth-storage
  const raw = localStorage.getItem('auth-storage');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      console.log('[getToken] auth-storage parsed keys:', Object.keys(parsed));

      const state = parsed.state || parsed;
      console.log('[getToken] state keys:', Object.keys(state));

      // Chercher token dans state
      if (state.token && typeof state.token === 'string' && state.token.length > 10) {
        console.log('[getToken] Token trouvé dans state.token');
        return state.token;
      }
      if (state.accessToken && typeof state.accessToken === 'string' && state.accessToken.length > 10) {
        console.log('[getToken] Token trouvé dans state.accessToken');
        return state.accessToken;
      }
      if (state.auth_token && typeof state.auth_token === 'string' && state.auth_token.length > 10) {
        console.log('[getToken] Token trouvé dans state.auth_token');
        return state.auth_token;
      }

      // Chercher dans user (token peut être dans user.token)
      if (state.user && typeof state.user === 'object') {
        console.log('[getToken] user keys:', Object.keys(state.user));
        if (state.user.token && typeof state.user.token === 'string' && state.user.token.length > 10) {
          console.log('[getToken] Token trouvé dans state.user.token');
          return state.user.token;
        }
        if (state.user.accessToken && typeof state.user.accessToken === 'string' && state.user.accessToken.length > 10) {
          console.log('[getToken] Token trouvé dans state.user.accessToken');
          return state.user.accessToken;
        }
      }

      // Afficher toutes les propriétés string > 10 chars de state
      for (const [k, v] of Object.entries(state)) {
        if (typeof v === 'string' && v.length > 20) {
          console.log(`[getToken] Prop string longue: ${k} = ${v.substring(0, 30)}...`);
        }
      }

    } catch (e) {
      console.log('[getToken] Erreur parsing auth-storage:', e);
    }
  }

  // 3. sessionStorage
  for (const key of LS_TOKEN_KEYS) {
    const val = sessionStorage.getItem(key);
    if (val && val !== 'null' && val !== 'undefined' && val.length > 10) {
      console.log('[getToken] Token trouvé dans sessionStorage');
      return val;
    }
  }

  console.log('[getToken] AUCUN TOKEN TROUVÉ');
  return '';
}
const COLORS = ['#1e3a5f', '#2d5a3d', '#8b4513', '#4a5568', '#c53030', '#2b6cb0', '#d69e2e', '#805ad5'];

// ============================================================================
// COMPONENT
// ============================================================================

export default function BudgetPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [budgetYears, setBudgetYears] = useState<BudgetYear[]>([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState<number | null>(null);
  const [currentBudget, setCurrentBudget] = useState<BudgetYear | null>(null);

  // APEE state
  const [mainLines, setMainLines] = useState<BudgetMainLine[]>([]);
  const [expenses, setExpenses] = useState<BudgetExpense[]>([]);
  const [apeeStats, setApeeStats] = useState<BudgetStats | null>(null);

  // Fees state
  const [feeLines, setFeeLines] = useState<BudgetFeeLine[]>([]);
  const [feeExpenses, setFeeExpenses] = useState<BudgetFeeExpense[]>([]);
  const [feeStats, setFeeStats] = useState<FeeStats | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showFeeExpenseModal, setShowFeeExpenseModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingFeeLine, setEditingFeeLine] = useState<BudgetFeeLine | null>(null);
  const [selectedMainLine, setSelectedMainLine] = useState<BudgetMainLine | null>(null);
  const [selectedFeeLine, setSelectedFeeLine] = useState<BudgetFeeLine | null>(null);

  // Form state
  const [expenseForm, setExpenseForm] = useState({
    expense_number: '', name: '', description: '', quantity: 1,
    unit_price: 0, total_amount: 0, expense_date: new Date().toISOString().split('T')[0],
    payment_method: '', beneficiary: '', receipt_number: '', sub_line_id: null as number | null
  });
  const [feeExpenseForm, setFeeExpenseForm] = useState({
    expense_number: '', name: '', quantity: 1, unit_price: 0,
    total_amount: 0, expense_date: new Date().toISOString().split('T')[0],
    payment_method: '', beneficiary: '', receipt_number: ''
  });
  const [budgetForm, setBudgetForm] = useState({
    name: '', description: '', school_id: user?.school_id || 0, academic_year_id: 0
  });

  // ============================================================================
  // API HELPERS
  // ============================================================================

  async function apiFetch(url: string, options?: RequestInit) {
    const currentToken = getToken();
    if (!currentToken) {
      throw new Error('Non authentifié — veuillez vous reconnecter');
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${currentToken}`,
    };
    if (options?.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }
    const res = await fetch(`${API_BASE}${url}`, { ...options, headers });
    if (res.status === 401) {
      throw new Error('Session expirée (401) — veuillez vous reconnecter');
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // ============================================================================
  // LOADERS
  // ============================================================================

  async function loadBudgetYears() {
    if (!user?.school_id) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/budget/years?school_id=${user.school_id}`);
      setBudgetYears(data);
      if (data.length > 0 && !selectedBudgetId) {
        setSelectedBudgetId(data[0].id);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadBudgetData(budgetId: number) {
    setLoading(true);
    try {
      const [budget, lines, exps, fLines, fExps, aStats, fStats] = await Promise.all([
        apiFetch(`/budget/years/${budgetId}`),
        apiFetch(`/budget/years/${budgetId}/lines`),
        apiFetch(`/budget/years/${budgetId}/expenses`),
        apiFetch(`/budget/years/${budgetId}/fee-lines`),
        apiFetch(`/budget/years/${budgetId}/fee-expenses`),
        apiFetch(`/budget/years/${budgetId}/stats/apee`),
        apiFetch(`/budget/years/${budgetId}/stats/fees`)
      ]);
      setCurrentBudget(budget);
      setMainLines(lines);
      setExpenses(exps);
      setFeeLines(fLines);
      setFeeExpenses(fExps);
      setApeeStats(aStats);
      setFeeStats(fStats);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadBudgetYears(); }, [user?.school_id]);
  useEffect(() => {
    if (selectedBudgetId) loadBudgetData(selectedBudgetId);
  }, [selectedBudgetId]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const createBudget = async () => {
    try {
      await apiFetch('/budget/years', {
        method: 'POST',
        body: JSON.stringify(budgetForm)
      });
      setShowBudgetModal(false);
      loadBudgetYears();
    } catch (e: any) { setError(e.message); }
  };

  const createExpense = async () => {
    if (!selectedBudgetId || !selectedMainLine) return;
    try {
      await apiFetch('/budget/expenses', {
        method: 'POST',
        body: JSON.stringify({
          ...expenseForm,
          budget_year_id: selectedBudgetId,
          main_line_id: selectedMainLine.id,
          total_amount: expenseForm.quantity * expenseForm.unit_price
        })
      });
      setShowExpenseModal(false);
      loadBudgetData(selectedBudgetId);
    } catch (e: any) { setError(e.message); }
  };

  const createFeeExpense = async () => {
    if (!selectedBudgetId || !selectedFeeLine) return;
    try {
      await apiFetch('/budget/fee-expenses', {
        method: 'POST',
        body: JSON.stringify({
          ...feeExpenseForm,
          budget_year_id: selectedBudgetId,
          fee_line_id: selectedFeeLine.id,
          total_amount: feeExpenseForm.quantity * feeExpenseForm.unit_price
        })
      });
      setShowFeeExpenseModal(false);
      loadBudgetData(selectedBudgetId);
    } catch (e: any) { setError(e.message); }
  };

  const updateFeeLine = async () => {
    if (!editingFeeLine) return;
    try {
      await apiFetch(`/budget/fee-lines/${editingFeeLine.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          rate_1st_cycle: editingFeeLine.rate_1st_cycle,
          rate_2nd_cycle: editingFeeLine.rate_2nd_cycle,
          students_1st_cycle: editingFeeLine.students_1st_cycle,
          students_2nd_cycle: editingFeeLine.students_2nd_cycle,
          is_mandatory: editingFeeLine.is_mandatory
        })
      });
      setEditingFeeLine(null);
      if (selectedBudgetId) loadBudgetData(selectedBudgetId);
    } catch (e: any) { setError(e.message); }
  };

  const verifyExpense = async (id: number, type: 'apee' | 'fee') => {
    try {
      await apiFetch(`/budget/${type === 'apee' ? 'expenses' : 'fee-expenses'}/${id}/verify`, { method: 'POST' });
      if (selectedBudgetId) loadBudgetData(selectedBudgetId);
    } catch (e: any) { setError(e.message); }
  };

  const deleteExpense = async (id: number, type: 'apee' | 'fee') => {
    if (!confirm('Confirmer la suppression ?')) return;
    try {
      await apiFetch(`/budget/${type === 'apee' ? 'expenses' : 'fee-expenses'}/${id}`, { method: 'DELETE' });
      if (selectedBudgetId) loadBudgetData(selectedBudgetId);
    } catch (e: any) { setError(e.message); }
  };

  const generateReport = async (type: 'conseil' | 'projet' | 'rapprochement') => {
    if (!selectedBudgetId) return;
    try {
      let currentToken = '';
      for (const key of LS_TOKEN_KEYS) {
        const val = localStorage.getItem(key);
        if (val && val !== 'null' && val !== 'undefined') { currentToken = val; break; }
      }
      const res = await fetch(`${API_BASE}/budget/years/${selectedBudgetId}/reports/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });
      if (!res.ok) throw new Error('Erreur génération PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_${type}_${selectedBudgetId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) { setError(e.message); }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const formatFCFA = (n: number) => `${(n || 0).toLocaleString('fr-FR')} FCFA`;
  const formatPct = (n: number) => `${(n || 0).toFixed(2)} %`;

  const getExecutionColor = (rate: number) => {
    if (rate >= 90) return 'text-red-600';
    if (rate >= 75) return 'text-amber-600';
    return 'text-emerald-600';
  };

  const filteredMainLines = mainLines.filter(l =>
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFeeLines = feeLines.filter(l =>
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet className="h-7 w-7 text-blue-700" />
              Gestion Budgétaire
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Lycée Bilingue de Baleng — Budget APEE & Frais Exigibles
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedBudgetId || ''}
              onChange={e => setSelectedBudgetId(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner un budget...</option>
              {budgetYears.map(b => (
                <option key={b.id} value={b.id}>{b.name} ({b.status})</option>
              ))}
            </select>
            <button
              onClick={() => setShowBudgetModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 text-sm font-medium"
            >
              <Plus className="h-4 w-4" /> Nouveau budget
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertTriangle className="h-4 w-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {loading && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center gap-2">
          <Activity className="h-4 w-4 animate-spin" /> Chargement...
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="flex overflow-x-auto border-b border-gray-200">
          {[
            { id: 'overview' as TabType, label: 'Vue d\'ensemble', icon: BarChart3 },
            { id: 'apee-lines' as TabType, label: 'Rubriques APEE', icon: BookOpen },
            { id: 'apee-expenses' as TabType, label: 'Dépenses APEE', icon: Receipt },
            { id: 'fees' as TabType, label: 'Frais Exigibles', icon: DollarSign },
            { id: 'reports' as TabType, label: 'Rapports Officiels', icon: FileText },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-700 text-blue-700 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ── TAB: OVERVIEW ── */}
          {activeTab === 'overview' && currentBudget && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-xs">Budget APEE — Prévisions</p>
                      <p className="text-2xl font-bold">{formatFCFA(apeeStats?.total_planned || 0)}</p>
                    </div>
                    <Wallet className="h-8 w-8 text-blue-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-xs">Budget APEE — Réalisations</p>
                      <p className="text-2xl font-bold">{formatFCFA(apeeStats?.total_actual || 0)}</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-emerald-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-xs">Frais Exigibles — Total</p>
                      <p className="text-2xl font-bold">{formatFCFA(feeStats?.total_planned || 0)}</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-amber-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-xs">Taux d\'exécution global</p>
                      <p className="text-2xl font-bold">{formatPct(apeeStats?.execution_rate || 0)}</p>
                    </div>
                    <Activity className="h-8 w-8 text-purple-200" />
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-800 mb-4">Répartition APEE par rubrique</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={mainLines.map(l => ({ name: l.name, value: Number(l.planned_amount) || 0 }))}
                        cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                        paddingAngle={2} dataKey="value"
                      >
                        {mainLines.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => formatFCFA(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-800 mb-4">Exécution APEE vs Prévisions</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={mainLines.slice(0, 8)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="code" />
                      <YAxis tickFormatter={v => `${v/1000}k`} />
                      <Tooltip formatter={(v: number) => formatFCFA(v)} />
                      <Legend />
                      <Bar dataKey="planned_amount" name="Prévu" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="actual_amount" name="Réalisé" fill="#2d5a3d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Frais Exigibles Summary */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-800 mb-4">Frais Exigibles par élève</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-blue-700">1er cycle</p>
                    <p className="text-xl font-bold text-blue-900">{formatFCFA(feeStats?.rate_1st_cycle_total || 0)}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-emerald-700">2nd cycle</p>
                    <p className="text-xl font-bold text-emerald-900">{formatFCFA(feeStats?.rate_2nd_cycle_total || 0)}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-purple-700">Total prévisions</p>
                    <p className="text-xl font-bold text-purple-900">{formatFCFA(feeStats?.total_planned || 0)}</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={feeLines.slice(1, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="code" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => formatFCFA(v)} />
                    <Bar dataKey="rate_1st_cycle" name="1er cycle" fill="#3b82f6" />
                    <Bar dataKey="rate_2nd_cycle" name="2nd cycle" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* ── TAB: APEE LINES ── */}
          {activeTab === 'apee-lines' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Rubriques budgétaires APEE ({mainLines.length})</h3>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64"
                  />
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Code</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Rubrique</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Prévisions</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Réalisations</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Restant</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">%</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredMainLines.map(line => {
                      const pct = line.planned_amount > 0 ? (line.actual_amount / line.planned_amount) * 100 : 0;
                      return (
                        <tr key={line.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-gray-600">{line.code}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{line.name}</div>
                            {line.description && <div className="text-xs text-gray-500">{line.description}</div>}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{formatFCFA(line.planned_amount)}</td>
                          <td className="px-4 py-3 text-right">{formatFCFA(line.actual_amount)}</td>
                          <td className={`px-4 py-3 text-right font-medium ${line.remaining_amount < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                            {formatFCFA(line.remaining_amount)}
                          </td>
                          <td className={`px-4 py-3 text-center font-medium ${getExecutionColor(pct)}`}>
                            {formatPct(pct)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => { setSelectedMainLine(line); setShowExpenseModal(true); }}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                            >
                              <Plus className="h-3 w-3" /> Dépense
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-right">TOTAL</td>
                      <td className="px-4 py-3 text-right">{formatFCFA(apeeStats?.total_planned || 0)}</td>
                      <td className="px-4 py-3 text-right">{formatFCFA(apeeStats?.total_actual || 0)}</td>
                      <td className="px-4 py-3 text-right">{formatFCFA(apeeStats?.total_remaining || 0)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* ── TAB: APEE EXPENSES ── */}
          {activeTab === 'apee-expenses' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Dépenses APEE ({expenses.length})</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                    {apeeStats?.pending_expenses || 0} en attente
                  </span>
                  <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                    {apeeStats?.verified_expenses || 0} vérifiées
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">N°</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Date</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Description</th>
                      <th className="px-3 py-3 text-right font-semibold text-gray-700">Montant</th>
                      <th className="px-3 py-3 text-center font-semibold text-gray-700">Statut</th>
                      <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {expenses.map(exp => (
                      <tr key={exp.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 font-mono text-xs text-gray-500">{exp.expense_number}</td>
                        <td className="px-3 py-3 text-gray-600">{exp.expense_date}</td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-900">{exp.name}</div>
                          <div className="text-xs text-gray-500">{exp.beneficiary}</div>
                        </td>
                        <td className="px-3 py-3 text-right font-medium">{formatFCFA(exp.total_amount)}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            exp.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                            exp.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            exp.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {exp.status === 'verified' && <CheckCircle className="h-3 w-3" />}
                            {exp.status}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {exp.status === 'pending' && (
                              <button
                                onClick={() => verifyExpense(exp.id, 'apee')}
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Vérifier"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteExpense(exp.id, 'apee')}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── TAB: FRAIS EXIGIBLES ── */}
          {activeTab === 'fees' && (
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-800">Frais exigibles par élève</h3>
                  <p className="text-sm text-gray-500">24 lignes de frais — Tarification par cycle</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-medium">
                    1er cycle: {formatFCFA(feeStats?.rate_1st_cycle_total || 0)}
                  </span>
                  <span className="text-sm bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg font-medium">
                    2nd cycle: {formatFCFA(feeStats?.rate_2nd_cycle_total || 0)}
                  </span>
                </div>
              </div>

              {/* Fee Lines Table */}
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-emerald-50">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold text-emerald-900">Code</th>
                      <th className="px-3 py-3 text-left font-semibold text-emerald-900">Libellé</th>
                      <th className="px-3 py-3 text-right font-semibold text-emerald-900">Taux 1er</th>
                      <th className="px-3 py-3 text-right font-semibold text-emerald-900">Taux 2nd</th>
                      <th className="px-3 py-3 text-right font-semibold text-emerald-900">Prévisions</th>
                      <th className="px-3 py-3 text-right font-semibold text-emerald-900">Dépensé</th>
                      <th className="px-3 py-3 text-right font-semibold text-emerald-900">Restant</th>
                      <th className="px-3 py-3 text-center font-semibold text-emerald-900">%</th>
                      <th className="px-3 py-3 text-center font-semibold text-emerald-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredFeeLines.map(line => {
                      const pct = line.planned_amount > 0 ? (line.actual_amount / line.planned_amount) * 100 : 0;
                      return (
                        <tr key={line.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 font-mono text-gray-600">{line.code}</td>
                          <td className="px-3 py-3">
                            <div className="font-medium text-gray-900">{line.name}</div>
                            {!line.is_mandatory && <span className="text-xs text-gray-400">(facultatif)</span>}
                          </td>
                          <td className="px-3 py-3 text-right">{formatFCFA(line.rate_1st_cycle)}</td>
                          <td className="px-3 py-3 text-right">{formatFCFA(line.rate_2nd_cycle)}</td>
                          <td className="px-3 py-3 text-right font-medium">{formatFCFA(line.planned_amount)}</td>
                          <td className="px-3 py-3 text-right">{formatFCFA(line.actual_amount)}</td>
                          <td className={`px-3 py-3 text-right font-medium ${line.remaining_amount < 0 ? 'text-red-600' : ''}`}>
                            {formatFCFA(line.remaining_amount)}
                          </td>
                          <td className={`px-3 py-3 text-center font-medium ${getExecutionColor(pct)}`}>
                            {formatPct(pct)}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => { setSelectedFeeLine(line); setShowFeeExpenseModal(true); }}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Ajouter dépense"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setEditingFeeLine(line)}
                                className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                                title="Modifier tarifs"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-emerald-50 font-semibold">
                    <tr>
                      <td colSpan={4} className="px-3 py-3 text-right text-emerald-900">TOTAL</td>
                      <td className="px-3 py-3 text-right text-emerald-900">{formatFCFA(feeStats?.total_planned || 0)}</td>
                      <td className="px-3 py-3 text-right text-emerald-900">{formatFCFA(feeStats?.total_actual || 0)}</td>
                      <td className="px-3 py-3 text-right text-emerald-900">{formatFCFA(feeStats?.total_remaining || 0)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Fee Expenses Detail */}
              {feeExpenses.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-3">Détail des dépenses frais exigibles</h4>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">N°</th>
                          <th className="px-3 py-2 text-left">Ligne</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-right">Montant</th>
                          <th className="px-3 py-2 text-center">Vérifié</th>
                          <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {feeExpenses.map(exp => {
                          const line = feeLines.find(l => l.id === exp.fee_line_id);
                          return (
                            <tr key={exp.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-mono text-xs text-gray-500">{exp.expense_number}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{line?.code} {line?.name}</td>
                              <td className="px-3 py-2">{exp.name}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatFCFA(exp.total_amount)}</td>
                              <td className="px-3 py-2 text-center">
                                {exp.is_verified ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-600 mx-auto" />
                                ) : (
                                  <span className="text-xs text-amber-600">Non</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {!exp.is_verified && (
                                    <button
                                      onClick={() => verifyExpense(exp.id, 'fee')}
                                      className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => deleteExpense(exp.id, 'fee')}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── TAB: REPORTS ── */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              <h3 className="font-semibold text-gray-800">Rapports officiels</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Rapport Conseil</h4>
                      <p className="text-xs text-gray-500">APEE + Frais Exigibles</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Document officiel pour le Conseil d\'Établissement fusionnant les deux budgets.
                  </p>
                  <button
                    onClick={() => generateReport('conseil')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 text-sm font-medium"
                  >
                    <FileDown className="h-4 w-4" /> Générer PDF
                  </button>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Printer className="h-6 w-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Projet de Budget</h4>
                      <p className="text-xs text-gray-500">Prévisions APEE uniquement</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Présentation des 14 lignes budgétaires avec prévisions et sous-rubriques.
                  </p>
                  <button
                    onClick={() => generateReport('projet')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 text-sm font-medium"
                  >
                    <FileDown className="h-4 w-4" /> Générer PDF
                  </button>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <ArrowLeftRight className="h-6 w-6 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Rapprochement</h4>
                      <p className="text-xs text-gray-500">Frais Exigibles — Paysage</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    État de rapprochement des comptes : prévisions vs réalisations par ligne.
                  </p>
                  <button
                    onClick={() => generateReport('rapprochement')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 text-sm font-medium"
                  >
                    <FileDown className="h-4 w-4" /> Générer PDF
                  </button>
                </div>
              </div>

              {/* Preview info */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  <School className="h-5 w-5 text-blue-700 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">Informations du rapport</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Budget sélectionné : <strong>{currentBudget?.name || 'Aucun'}</strong><br />
                      Généré par : <strong>{user?.first_name} {user?.last_name}</strong><br />
                      Date : <strong>{new Date().toLocaleDateString('fr-FR')}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================================
          MODALS
      ============================================================================ */}

      {/* Modal: New Budget */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Nouveau budget</h3>
              <button onClick={() => setShowBudgetModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={budgetForm.name}
                  onChange={e => setBudgetForm({ ...budgetForm, name: e.target.value })}
                  placeholder="ex: Budget APEE 2025-2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={budgetForm.description}
                  onChange={e => setBudgetForm({ ...budgetForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Année académique ID</label>
                <input
                  type="number"
                  value={budgetForm.academic_year_id}
                  onChange={e => setBudgetForm({ ...budgetForm, academic_year_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowBudgetModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={createBudget}
                  className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 text-sm font-medium"
                >
                  Créer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: APEE Expense */}
      {showExpenseModal && selectedMainLine && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Nouvelle dépense — {selectedMainLine.name}</h3>
              <button onClick={() => setShowExpenseModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">N° pièce</label>
                  <input
                    type="text"
                    value={expenseForm.expense_number}
                    onChange={e => setExpenseForm({ ...expenseForm, expense_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={e => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={expenseForm.name}
                  onChange={e => setExpenseForm({ ...expenseForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Qté</label>
                  <input
                    type="number"
                    value={expenseForm.quantity}
                    onChange={e => setExpenseForm({ ...expenseForm, quantity: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Prix unit.</label>
                  <input
                    type="number"
                    value={expenseForm.unit_price}
                    onChange={e => setExpenseForm({ ...expenseForm, unit_price: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Total</label>
                  <input
                    type="number"
                    value={expenseForm.quantity * expenseForm.unit_price}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bénéficiaire</label>
                  <input
                    type="text"
                    value={expenseForm.beneficiary}
                    onChange={e => setExpenseForm({ ...expenseForm, beneficiary: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">N° Facture</label>
                  <input
                    type="text"
                    value={expenseForm.receipt_number}
                    onChange={e => setExpenseForm({ ...expenseForm, receipt_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowExpenseModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={createExpense}
                  className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 text-sm font-medium"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Fee Expense */}
      {showFeeExpenseModal && selectedFeeLine && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Dépense — {selectedFeeLine.name}</h3>
              <button onClick={() => setShowFeeExpenseModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">N° pièce</label>
                  <input
                    type="text"
                    value={feeExpenseForm.expense_number}
                    onChange={e => setFeeExpenseForm({ ...feeExpenseForm, expense_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={feeExpenseForm.expense_date}
                    onChange={e => setFeeExpenseForm({ ...feeExpenseForm, expense_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={feeExpenseForm.name}
                  onChange={e => setFeeExpenseForm({ ...feeExpenseForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Qté</label>
                  <input
                    type="number"
                    value={feeExpenseForm.quantity}
                    onChange={e => setFeeExpenseForm({ ...feeExpenseForm, quantity: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Prix unit.</label>
                  <input
                    type="number"
                    value={feeExpenseForm.unit_price}
                    onChange={e => setFeeExpenseForm({ ...feeExpenseForm, unit_price: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Total</label>
                  <input
                    type="number"
                    value={feeExpenseForm.quantity * feeExpenseForm.unit_price}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowFeeExpenseModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={createFeeExpense}
                  className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 text-sm font-medium"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit Fee Line */}
      {editingFeeLine && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Modifier — {editingFeeLine.name}</h3>
              <button onClick={() => setEditingFeeLine(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Taux 1er cycle</label>
                  <input
                    type="number"
                    value={editingFeeLine.rate_1st_cycle}
                    onChange={e => setEditingFeeLine({ ...editingFeeLine, rate_1st_cycle: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Taux 2nd cycle</label>
                  <input
                    type="number"
                    value={editingFeeLine.rate_2nd_cycle}
                    onChange={e => setEditingFeeLine({ ...editingFeeLine, rate_2nd_cycle: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Effectif 1er</label>
                  <input
                    type="number"
                    value={editingFeeLine.students_1st_cycle}
                    onChange={e => setEditingFeeLine({ ...editingFeeLine, students_1st_cycle: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Effectif 2nd</label>
                  <input
                    type="number"
                    value={editingFeeLine.students_2nd_cycle}
                    onChange={e => setEditingFeeLine({ ...editingFeeLine, students_2nd_cycle: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingFeeLine.is_mandatory}
                  onChange={e => setEditingFeeLine({ ...editingFeeLine, is_mandatory: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">Obligatoire</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setEditingFeeLine(null)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={updateFeeLine}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                >
                  <Save className="h-4 w-4 inline mr-1" /> Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}